import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, Typography, Input, Button, Textarea } from "@material-tailwind/react";
import axios from 'axios';
import { rootUrl } from '../../../../config/config';
import { useAuth } from '../../AuthContext';
import { Alerty } from '../../../common/Alerty';

const PostDriverTransaction = () => {
    const { user } = useAuth();
    const [drivers, setDrivers] = useState([]);
    const [transTypes, setTransTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [successOpen, setSuccessOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        driver_id: '',
        trans_type: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        notes: '',
        your_ref: '',
        our_ref: ''
    });

    // (car field removed) helper functions were omitted

    useEffect(() => {
        fetchDrivers();
        fetchTransTypes();
    }, []);

    const fetchDrivers = async () => {
        try {
            const res = await axios.get(`${rootUrl}/drivers?limit=1000`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setDrivers(res.data.data || []);
        } catch (error) {
            console.error('Error fetching drivers:', error);
        }
    };

    const fetchTransTypes = async () => {
        try {
            const res = await axios.get(`${rootUrl}/payments/driver-types?car_post=false`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setTransTypes(res.data.data || []);
        } catch (error) {
            console.error('Error fetching transaction types:', error);
        }
    };

    // cars list removed - no fetch needed

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }

        // no car auto-fill after removal of car field
    };

    // Simple reusable searchable select component
    const SearchableSelect = ({ label, options, value, onChange, optionToLabel, valueKey = (o) => o }) => {
        const [open, setOpen] = useState(false);
        const [query, setQuery] = useState('');
        const ref = useRef();

        useEffect(() => {
            function handleClickOutside(e) {
                if (ref.current && !ref.current.contains(e.target)) setOpen(false);
            }
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        const selectedLabel = (() => {
            if (!value) return '';
            const found = options.find(o => (valueKey(o) === value));
            return found ? optionToLabel(found) : '';
        })();

        const filtered = options.filter(o => optionToLabel(o).toLowerCase().includes((query || selectedLabel).toLowerCase()));

        return (
            <div className="relative" ref={ref}>
                <Input
                    label={label}
                    value={open ? query : (selectedLabel || query)}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                />
                {open && (
                    <ul className="absolute left-0 right-0 z-50 bg-white border rounded mt-1 max-h-48 overflow-auto">
                        {filtered.length === 0 && <li className="p-2 text-sm text-gray-500">No options</li>}
                        {filtered.map((o, idx) => (
                            <li key={idx} className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onClick={() => { onChange(valueKey(o)); setOpen(false); setQuery(''); }}>
                                {optionToLabel(o)}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.driver_id) newErrors.driver_id = "Driver is required";
        if (!formData.date) newErrors.date = "Date is required";
        if (!formData.amount) newErrors.amount = "Amount is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                ...formData,
                input_by: user?.username || 'system'
            };

            const res = await axios.post(`${rootUrl}/drivers/transactions`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (res.data.success) {
                setSuccessMessage(`Transaction posted! Doc No: ${res.data.docNo}`);
                setSuccessOpen(true);
                setFormData({
                    ...formData,
                    amount: '',
                    notes: '',
                    your_ref: '',
                    our_ref: ''
                });
            }
        } catch (error) {
            console.error('Error posting transaction:', error);
            setErrorMessage(error.response?.data?.error || error.message);
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] min-h-screen p-6">
            <div className="max-w-4xl mx-auto">
                <Card className="bg-white rounded-2xl shadow-xl p-6">
                    <CardBody>
                        <Typography variant="h4" color="blue-gray" className="mb-6">
                            Post Driver Transaction
                        </Typography>

                        <Alerty open={successOpen} setOpen={setSuccessOpen} color="green" message={successMessage} variant="solid" />
                        <Alerty open={errorOpen} setOpen={setErrorOpen} color="red" message={errorMessage} variant="solid" />

                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                            <div>
                                <SearchableSelect
                                    label="Driver"
                                    options={drivers}
                                    value={formData.driver_id}
                                    onChange={(val) => handleSelectChange('driver_id', val)}
                                    optionToLabel={(d) => `${d.name || d.driver_name || ''} (${d.id_no || d.id || ''})`}
                                    valueKey={(d) => d.id_no}
                                />
                                {errors.driver_id && <Typography color="red" className="text-xs mt-1">{errors.driver_id}</Typography>}
                            </div>



                            <div>
                                <SearchableSelect
                                    label="Transaction Type"
                                    options={transTypes}
                                    value={formData.trans_type}
                                    onChange={(val) => handleSelectChange('trans_type', val)}
                                    optionToLabel={(t) => t.short_desc}
                                    valueKey={(t) => t.type}
                                />
                                {errors.trans_type && <Typography color="red" className="text-xs mt-1">{errors.trans_type}</Typography>}
                            </div>

                            <div>
                                <Input
                                    type="date"
                                    label="Date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    error={errors.date}
                                />
                                {errors.date && <Typography color="red" className="text-xs mt-1">{errors.date}</Typography>}
                            </div>

                            <div>
                                <Input
                                    type="number"
                                    label="Amount"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    error={errors.amount}
                                />
                                {errors.amount && <Typography color="red" className="text-xs mt-1">{errors.amount}</Typography>}
                            </div>

                            <div>
                                <Input
                                    label="Your Ref"
                                    name="your_ref"
                                    value={formData.your_ref}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <Textarea
                                    label="Notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="md:col-span-2 flex justify-end">
                                <Button type="submit" color="orange" disabled={loading}>
                                    {loading ? 'Posting...' : 'Post Transaction'}
                                </Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default PostDriverTransaction;